import React, { useState } from "react";
import { Box, Typography, Dialog, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import { FormattedMessage } from "react-intl";

// Read-only gallery of a job offer's optional photos and videos.
// Expects jobOffer.job_offer_images_records / job_offer_videos_records
// (each record has a `url`). Renders nothing when there is no media.
const JobOfferMediaGallery = ({ jobOffer, sx = {} }) => {
  const images = jobOffer?.job_offer_images_records || [];
  const videos = jobOffer?.job_offer_videos_records || [];

  const [lightbox, setLightbox] = useState(null); // { type, url }

  if (images.length === 0 && videos.length === 0) return null;

  const tileSx = {
    position: "relative",
    width: { xs: "100%", sm: "160px" },
    height: { xs: "200px", sm: "120px" },
    borderRadius: "8px",
    overflow: "hidden",
    border: "1px solid hsl(160, 10%, 90%)",
    cursor: "pointer",
    "& img, & video": {
      width: "100%",
      height: "100%",
      objectFit: "cover",
      display: "block",
    },
  };

  return (
    <Box sx={{ width: "100%", ...sx }}>
      <Typography variant="h5" sx={{ mb: "12px" }}>
        <FormattedMessage id="job_offer_creation.media.gallery_title" />
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
        {images.map((record, index) => (
          <Box
            key={`img-${index}`}
            sx={tileSx}
            onClick={() => setLightbox({ type: "image", url: record.url })}
          >
            <img src={record.url} alt={`Job offer photo ${index + 1}`} loading="lazy" />
          </Box>
        ))}
        {videos.map((record, index) => (
          <Box
            key={`vid-${index}`}
            sx={tileSx}
            onClick={() => setLightbox({ type: "video", url: record.url })}
          >
            <video src={record.url} muted playsInline preload="metadata" />
            <PlayCircleOutlineIcon
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                color: "rgba(255,255,255,0.9)",
                fontSize: "40px",
                pointerEvents: "none",
              }}
            />
          </Box>
        ))}
      </Box>

      <Dialog
        open={Boolean(lightbox)}
        onClose={() => setLightbox(null)}
        maxWidth="md"
        fullWidth
      >
        <Box sx={{ position: "relative", background: "#000" }}>
          <IconButton
            onClick={() => setLightbox(null)}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 1,
              color: "#fff",
              background: "rgba(0,0,0,0.4)",
            }}
          >
            <CloseIcon />
          </IconButton>
          {lightbox?.type === "image" ? (
            <img
              src={lightbox.url}
              alt="Job offer media"
              style={{
                maxWidth: "100%",
                maxHeight: "80vh",
                width: "auto",
                height: "auto",
                display: "block",
                margin: "0 auto",
              }}
            />
          ) : lightbox?.type === "video" ? (
            <video
              src={lightbox.url}
              controls
              autoPlay
              style={{
                maxWidth: "100%",
                maxHeight: "80vh",
                width: "auto",
                height: "auto",
                display: "block",
                margin: "0 auto",
              }}
            />
          ) : null}
        </Box>
      </Dialog>
    </Box>
  );
};

export default JobOfferMediaGallery;
